import React, { memo, useEffect, useState, useCallback, useRef } from 'react';
import { FieldVisionAppWrapper, FieldVisionApp, GamedayLiveMobileWrapper, GamedayMobileTeamMatchupWrapper } from './fieldvision.style';
import FieldVisionButtonDataContainer from './containers/FieldVisionButtonDataContainer';
import { liveViewsTypes } from '../themes/liveViewsTypes';
import changeViewAction from '@gameday/app/lib/store/actions/views';
import { useDispatch, useSelector } from 'react-redux';
import GamedayLiveDesktop from './components/GamedayLiveDesktop';
import GDBREAKPOINTS, { GDBREAKPOINTS_NUM } from '../themes/breakpoints';
import { useMedia } from '@mlb/react-patterns/v4/utils/hooks/useMedia/useMedia';
import TeamSummaryMatchUpDataContainer from '../TeamSummaryMatchup/TeamSummaryMatchupDataContainer';
import FieldVisionPlayerDuelDataContainer from '../PlayerDuel/containers/FieldVisionPlayerDuelDataContainer';
import { FieldVisionExploreModalContent, FieldVisionVerticalExploreModalContent } from '../Overlay/overlay.style';
import Overlay from '../Overlay/Overlay';
import { ModalTypes } from '../AtBat/atbat.style';

import Explore from './components/auth/Explore';
import { getFieldVisionState } from './store/selectors/getFieldVision';
import DataEvents from '@gameday/core/events/DataEvents';
import MessageService from 'message-service/message-service';
import useTracking from '@gameday/utils/hooks/useTracking';
import GamedayFieldVisionPlayer from './components/GamedayFieldVisionPlayer';
import GameOverDataContainer from '../GameOver/containers/GameOverDataContainer';
import ErrorBoundaryWrapper from '@gameday/components/ErrorFallback/ErrorBoundaryWrapper';

const { UPDATE_EXTERNAL } = DataEvents;

const {
    XL
} = GDBREAKPOINTS;

const trackingConfig = ['FieldVision', 'TeamSummaryMatchup'];

const { FULL, TWOCOLS, SINGLE } = liveViewsTypes;

const FieldVision = () => {
    const { gamePk, accessToken, enableFieldVision, forcedFieldVisionOn } = useSelector(getFieldVisionState);
    const controllerRef = useRef();
    const Tracking = useTracking();
    const cameraMenuPortalRef = React.useRef(null);
    const [isTutorialOpen, setTutorialOpen] = useState(false);
    const onTutorialClose = () => setTutorialOpen(false);
    const isFullscreen = useMedia({ minWidth: XL });
    const isTwoCols = useMedia({ maxWidth: `${GDBREAKPOINTS_NUM.XL - 1}px`, minWidth: `${GDBREAKPOINTS_NUM.MD}px` });
    const isSingleCol = useMedia({ maxWidth: `${GDBREAKPOINTS_NUM.MD - 1}px` });
    const dispatch = useDispatch();
    let viewType = FULL;

    if (isFullscreen) {
        viewType = FULL;
    } else if (isTwoCols) {
        viewType = TWOCOLS;
    } else if (isSingleCol) {
        viewType = SINGLE;
    };

    const showGameday = useCallback(() => {
        dispatch(changeViewAction({
            fieldVisionOn: false
        }));
        // tracking fv -> gameday
        Tracking.track.track_pageload();
    }, [changeViewAction]);

    useEffect(() => {
        // Add additional safety check if FV is on, and the enabled state changes
        // due to game switching, or another async event.
        if (!(enableFieldVision || forcedFieldVisionOn)) {
            dispatch(changeViewAction({
                fieldVisionOn: false
            }));
        }
    }, [enableFieldVision, forcedFieldVisionOn]);

    const onGumboChange = useCallback((data) => {
        if (typeof data === 'string') {
            MessageService.publish(UPDATE_EXTERNAL, { timecode: data });
        } else {
            MessageService.publish(UPDATE_EXTERNAL, data);
        }
    }, []);

    return (
        <ErrorBoundaryWrapper componentName='Field Vision'>
            <FieldVisionAppWrapper $type={viewType}>
                { !isSingleCol
                    ? <GamedayLiveDesktop />
                    : <GamedayMobileTeamMatchupWrapper>
                        <TeamSummaryMatchUpDataContainer
                            displayRecords={ true }
                            hideTeamNames={ false }
                            mobileWidth={ true }
                            useTri={true}
                            componentName={ trackingConfig }
                        /></GamedayMobileTeamMatchupWrapper>}

                <FieldVisionApp $type={viewType} ref={cameraMenuPortalRef}>
                    <FieldVisionButtonDataContainer showGameday={showGameday} $type={viewType} />
                    <GamedayFieldVisionPlayer
                        ref={controllerRef}
                        gamePk={gamePk * 1}
                        debug={false}
                        accessToken={accessToken}
                        onGumboChange={onGumboChange}
                        embeddedControls={'live'}
                        key={gamePk + accessToken}
                        cameraIntro={true}
                    />
                </FieldVisionApp>

                { isSingleCol && <GamedayLiveMobileWrapper><FieldVisionPlayerDuelDataContainer stacked showUpcomingBatters={true} /></GamedayLiveMobileWrapper> }
                <GameOverDataContainer />
                {<Overlay
                    isCentered={true}
                    isOpen={isTutorialOpen}
                    onClose={onTutorialClose}
                    type={!isSingleCol ? ModalTypes.FIELDVISION : ModalTypes.FIELDVISION_VERTICAL}
                    ContentComponent={!isSingleCol ? FieldVisionExploreModalContent : FieldVisionVerticalExploreModalContent}
                >
                    <Explore isSingleCol={isSingleCol}/>
                </Overlay>}
            </FieldVisionAppWrapper>
        </ErrorBoundaryWrapper>

    );
};

FieldVision.propTypes = {};

export default memo(FieldVision);
